// PRD.322 — Rich Text content model for the Form Builder.
//
// WHY A DOCUMENT MODEL AND NOT HTML.
//
// The repository has NO HTML sanitizer and NO rich-text editor dependency, and
// `dangerouslySetInnerHTML` appears nowhere. Storing HTML would mean adding a
// sanitizer and trusting it on every render path — builder canvas, preview,
// runtime form, print. Storing a RESTRICTED DOCUMENT instead makes the whole
// class of injection impossible rather than filtered: the renderer emits React
// elements from a closed set of node types, so there is no parsing step and
// nothing that could ever become markup. `<script>` in a document is text, and
// text is all it can ever be.
//
// This also keeps the PRD's rule — never sanitize HTML with regex — trivially
// satisfied, because no HTML is ever parsed here.
//
// Import-free so it runs under the node:test harness.

/** Inline marks the V1 toolbar can apply. Deliberately closed. */
export const RICH_TEXT_MARKS = ['bold', 'italic', 'underline'] as const;
export type RichTextMark = (typeof RICH_TEXT_MARKS)[number];

/** A run of text carrying zero or more marks. */
export interface RichTextSpan {
  text: string;
  marks?: RichTextMark[];
}

/**
 * Block types the V1 toolbar can produce.
 *
 * `paragraph` covers paragraphs and line breaks; lists are represented as a
 * block per item with a `list` kind, which keeps the model flat and makes
 * reordering and rendering straightforward.
 */
export const RICH_TEXT_BLOCK_TYPES = ['paragraph', 'bullet', 'number'] as const;
export type RichTextBlockType = (typeof RICH_TEXT_BLOCK_TYPES)[number];

export interface RichTextBlock {
  type: RichTextBlockType;
  spans: RichTextSpan[];
}

export interface RichTextDoc {
  /** Model version, so a future shape change can migrate rather than guess. */
  version: 1;
  blocks: RichTextBlock[];
}

export const EMPTY_RICH_TEXT: RichTextDoc = { version: 1, blocks: [] };

// ─── Parsing ───

function isMark(v: unknown): v is RichTextMark {
  return typeof v === 'string' && (RICH_TEXT_MARKS as readonly string[]).includes(v);
}

function parseSpan(raw: unknown): RichTextSpan | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as { text?: unknown; marks?: unknown };
  // Text is coerced, never dropped: losing a clinician's wording because of an
  // unexpected value type would be worse than rendering it plainly.
  const text = typeof o.text === 'string' ? o.text : '';
  if (!text) return null;
  const marks = Array.isArray(o.marks) ? o.marks.filter(isMark) : [];
  // Unknown marks are DISCARDED here, which is the whitelist: anything the
  // renderer does not know about cannot survive a round-trip.
  return marks.length > 0 ? { text, marks: [...new Set(marks)] } : { text };
}

function parseBlock(raw: unknown): RichTextBlock | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as { type?: unknown; spans?: unknown };
  const type = (RICH_TEXT_BLOCK_TYPES as readonly string[]).includes(String(o.type))
    ? (o.type as RichTextBlockType)
    : 'paragraph';
  const spans = Array.isArray(o.spans)
    ? o.spans.map(parseSpan).filter((s): s is RichTextSpan => s !== null)
    : [];
  return { type, spans };
}

/**
 * Read stored Rich Text. NEVER throws.
 *
 * Accepts three shapes so nothing a clinic has already typed is lost:
 *   - a serialized `RichTextDoc`
 *   - a bare JSON document object
 *   - PLAIN TEXT — anything unparseable becomes paragraphs, which is how an
 *     older textarea value or a hand-edited field degrades gracefully rather
 *     than rendering blank.
 */
export function parseRichText(raw: string | null | undefined): RichTextDoc {
  const value = (raw ?? '').trim();
  if (!value) return { version: 1, blocks: [] };

  if (value.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && Array.isArray((parsed as RichTextDoc).blocks)) {
        const blocks = (parsed as { blocks: unknown[] }).blocks
          .map(parseBlock)
          .filter((b): b is RichTextBlock => b !== null);
        return { version: 1, blocks };
      }
    } catch {
      // Fall through to the plain-text reading below.
    }
  }

  return fromPlainText(value);
}

/** Build a document from plain text, one paragraph per line. */
export function fromPlainText(text: string): RichTextDoc {
  return {
    version: 1,
    blocks: text.split(/\r?\n/).map((line) => ({
      type: 'paragraph' as const,
      spans: line ? [{ text: line }] : [],
    })),
  };
}

export function stringifyRichText(doc: RichTextDoc): string {
  return JSON.stringify({ version: 1, blocks: doc.blocks });
}

// ─── Inspection ───

/**
 * Whether the document has no meaningful content.
 *
 * STRUCTURAL emptiness, per the PRD: a document of empty paragraphs, or of
 * spans holding only whitespace, is empty. This is what `required` validation
 * checks, so a user cannot satisfy it by pressing Enter a few times.
 */
export function isRichTextEmpty(doc: RichTextDoc | null | undefined): boolean {
  if (!doc || doc.blocks.length === 0) return true;
  return doc.blocks.every((b) => b.spans.every((s) => s.text.trim() === ''));
}

/** Flatten to plain text — for print fallbacks, search and previews. */
export function richTextToPlainText(doc: RichTextDoc): string {
  return doc.blocks
    .map((b) => {
      const text = b.spans.map((s) => s.text).join('');
      if (b.type === 'bullet') return text ? `• ${text}` : '';
      return text;
    })
    .join('\n');
}

/** Character count of the rendered text — used for builder previews. */
export function richTextLength(doc: RichTextDoc): number {
  return doc.blocks.reduce((n, b) => n + b.spans.reduce((m, s) => m + s.text.length, 0), 0);
}

// ─── Instance snapshot ───

/**
 * Resolve what an EDITABLE Rich Text field should show for one patient.
 *
 * The stored instance value wins as soon as one exists, including when the
 * patient deliberately cleared it. Only a genuinely absent value (never
 * written) falls back to the definition default, which is the one-time
 * snapshot.
 *
 * This is the rule that stops a signed consent from silently re-wording itself
 * when an admin later edits the form definition: `null`/`undefined` means "no
 * snapshot taken yet", and empty string means "the patient's content is empty".
 */
export function resolveEditableRichText(
  storedValue: string | null | undefined,
  definitionDefault: string | null | undefined,
): RichTextDoc {
  if (storedValue === null || storedValue === undefined) {
    return parseRichText(definitionDefault);
  }
  return parseRichText(storedValue);
}

/** Whether an instance still needs its one-time snapshot of the default. */
export function needsRichTextSnapshot(storedValue: string | null | undefined): boolean {
  return storedValue === null || storedValue === undefined;
}
